import { useQuery } from '@tanstack/react-query'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDialog, useModalOverlay } from 'react-aria'
import { useOverlayTriggerState } from 'react-stately'
import { trpc } from '../../../../../utils/trpc'
import { type AggregateOption, useReportBuilderStore } from '../../stores/reportBuilderStore'
import {
	AggregationFunction,
	type AxisField,
	type ColumnField,
	GROUP_BY_PERIOD_OPTIONS,
	GROUPABLE_SYSTEM_FIELDS,
	type GroupByPeriod,
	type GroupBySource,
	QUESTION_LABEL_FIELD_ID,
} from '../../types'
import { CategoryManager } from '../CategoryManager'
import { ConfirmDialog } from '../ConfirmDialog'
import { IconButton } from '../IconButton'
import { PivotBuilder } from '../pivot'
import { CardinalityDisplay } from './CardinalityDisplay'

const COMPONENT_LABEL_PREFIX = 'component_label.'

const AGGREGATE_OPTIONS: { value: AggregateOption; label: string; description: string }[] = [
	{
		value: 'competency',
		label: 'Competency Averages',
		description: 'Calculate average ratings for each competency',
	},
	{
		value: 'overall_rating',
		label: 'Overall Rating Distribution',
		description: 'Show distribution of overall performance ratings',
	},
	{
		value: 'all_ratings',
		label: 'All Ratings',
		description: 'Include all individual rating responses',
	},
]

const MAX_GROUPING_LEVELS = 3

export function StepGrouping() {
	const {
		currentConfig,
		updateConfig,
		availableCustomFields,
		nextStep,
		prevStep,
		availableGroupCategories,
		loadComponentLabels,
		loadGroupCategories,
	} = useReportBuilderStore()

	useEffect(() => {
		if (currentConfig.selectedFormIds.length > 0) {
			loadComponentLabels(currentConfig.selectedFormIds)
			loadGroupCategories()
		}
	}, [currentConfig.selectedFormIds, loadComponentLabels, loadGroupCategories])

	const isRawDataMode = currentConfig.reportType === 'raw-data'

	// V2: Get rowFields or derive from V1 groupBy for backward compatibility
	const rowFields = useMemo((): AxisField[] => {
		if (currentConfig.rowFields && currentConfig.rowFields.length > 0) {
			return currentConfig.rowFields
		}
		// V1 backward compatibility: convert single groupBy to rowFields
		if (currentConfig.groupBy.length > 0) {
			return [
				{
					field: currentConfig.groupBy[0],
					source: currentConfig.groupBySource ?? 'custom_field',
					period: currentConfig.groupByPeriod,
					order: 0,
				},
			]
		}
		return []
	}, [currentConfig.rowFields, currentConfig.groupBy, currentConfig.groupBySource, currentConfig.groupByPeriod])

	// V2.5: Column fields for pivot/cross-tab
	const columnFields = useMemo((): ColumnField[] => {
		return currentConfig.columnFields || []
	}, [currentConfig.columnFields])

	const isPivotMode = columnFields.length > 0
	const [pivotEnabled, setPivotEnabled] = useState(isPivotMode)
	const categoryManagerState = useOverlayTriggerState({})
	const pivotOffConfirmState = useOverlayTriggerState({})
	const categoryManagerTriggerRef = useRef<HTMLButtonElement>(null)

	useEffect(() => {
		if (isRawDataMode && !currentConfig.aggregateBy.includes('raw')) {
			updateConfig({ aggregateBy: ['raw'] })
		}
	}, [isRawDataMode, currentConfig.aggregateBy, updateConfig])

	const toggleAggregate = (option: AggregateOption) => {
		const selected = currentConfig.aggregateBy.includes(option)
			? currentConfig.aggregateBy.filter((o) => o !== option)
			: [...currentConfig.aggregateBy, option]
		updateConfig({ aggregateBy: selected })
	}

	// Sorted custom fields with common ones first
	const commonFields = ['Title', 'Department', 'Location', 'Team']
	const sortedCustomFields = useMemo(
		() => [
			...commonFields.filter((f) => availableCustomFields.includes(f)),
			...availableCustomFields.filter((f) => !commonFields.includes(f)),
		],
		[availableCustomFields],
	)

	// Separate categorical and date system fields
	const categoricalSystemFields = useMemo(
		() => GROUPABLE_SYSTEM_FIELDS.filter((f) => f.groupType === 'categorical'),
		[],
	)
	const dateSystemFields = useMemo(() => GROUPABLE_SYSTEM_FIELDS.filter((f) => f.groupType === 'date_period'), [])

	// Get available fields for a given level (exclude already selected fields)
	const getAvailableFieldsForLevel = useCallback(
		(levelIndex: number) => {
			const selectedFields = rowFields.filter((_, i) => i !== levelIndex).map((f) => f.field)

			const availableCustom = sortedCustomFields.filter((f) => !selectedFields.includes(f))
			const availableCategorical = categoricalSystemFields.filter((f) => !selectedFields.includes(f.id))
			const availableDate = dateSystemFields.filter((f) => !selectedFields.includes(f.id))

			return { availableCustom, availableCategorical, availableDate }
		},
		[rowFields, sortedCustomFields, categoricalSystemFields, dateSystemFields],
	)

	// Sync V2 rowFields with V1 groupBy for backward compatibility
	const syncRowFieldsToV1 = useCallback(
		(newRowFields: AxisField[]) => {
			if (newRowFields.length === 0) {
				updateConfig({
					rowFields: [],
					groupBy: [],
					groupBySource: undefined,
					groupByPeriod: undefined,
					showSubtotals: false,
				})
			} else {
				const firstField = newRowFields[0]
				updateConfig({
					rowFields: newRowFields,
					groupBy: [firstField.field],
					groupBySource: firstField.source as 'custom_field' | 'system_field' | 'component_label',
					groupByPeriod: firstField.period,
				})
			}
		},
		[updateConfig],
	)

	// Handle field change at a specific level
	const handleFieldChange = useCallback(
		(levelIndex: number, value: string) => {
			const newRowFields = [...rowFields]

			if (!value) {
				// Remove this level and all levels below it
				newRowFields.splice(levelIndex)
			} else {
				// Check if it's a component label field
				const isComponentLabelField = value.startsWith(COMPONENT_LABEL_PREFIX)
				// Check if it's a system field
				const isSystemField = GROUPABLE_SYSTEM_FIELDS.some((f) => f.id === value)
				const systemField = GROUPABLE_SYSTEM_FIELDS.find((f) => f.id === value)

				let source: GroupBySource
				if (isComponentLabelField) {
					source = 'component_label'
				} else if (isSystemField) {
					source = 'system_field'
				} else {
					source = 'custom_field'
				}

				const period: GroupByPeriod | undefined = systemField?.groupType === 'date_period' ? 'month' : undefined

				newRowFields[levelIndex] = {
					field: isComponentLabelField ? value.slice(COMPONENT_LABEL_PREFIX.length) : value,
					source,
					period,
					order: levelIndex,
				}
			}

			syncRowFieldsToV1(newRowFields)
		},
		[rowFields, syncRowFieldsToV1],
	)

	// Handle period change for a specific level
	const handlePeriodChange = useCallback(
		(levelIndex: number, period: GroupByPeriod) => {
			const newRowFields = [...rowFields]
			if (newRowFields[levelIndex]) {
				newRowFields[levelIndex] = { ...newRowFields[levelIndex], period }
				syncRowFieldsToV1(newRowFields)
			}
		},
		[rowFields, syncRowFieldsToV1],
	)

	// Add a new grouping level
	const addLevel = useCallback(() => {
		if (rowFields.length >= MAX_GROUPING_LEVELS) return

		const newRowFields = [...rowFields, { field: '', source: 'custom_field' as GroupBySource, order: rowFields.length }]
		updateConfig({ rowFields: newRowFields })
	}, [rowFields, updateConfig])

	// Remove a grouping level and all levels below it
	const removeLevel = useCallback(
		(levelIndex: number) => {
			const newRowFields = rowFields.slice(0, levelIndex)
			syncRowFieldsToV1(newRowFields)
		},
		[rowFields, syncRowFieldsToV1],
	)

	// Toggle subtotals
	const toggleSubtotals = useCallback(() => {
		updateConfig({ showSubtotals: !currentConfig.showSubtotals })
	}, [currentConfig.showSubtotals, updateConfig])

	// V3.4: Build available fields list for PivotBuilder drag-and-drop
	const pivotAvailableFields = useMemo(() => {
		const fields: { id: string; label: string; source: GroupBySource }[] = []
		for (const f of sortedCustomFields) fields.push({ id: f, label: f, source: 'custom_field' })
		for (const f of GROUPABLE_SYSTEM_FIELDS) fields.push({ id: f.id, label: f.label, source: 'system_field' })
		fields.push({ id: QUESTION_LABEL_FIELD_ID, label: 'Form Question', source: 'component_label' })
		for (const c of availableGroupCategories) fields.push({ id: c, label: `Category: ${c}`, source: 'component_label' })
		return fields
	}, [sortedCustomFields, availableGroupCategories])

	// V3.4: Handle row fields change from PivotBuilder
	const handlePivotRowFieldsChange = useCallback(
		(fields: AxisField[]) => {
			syncRowFieldsToV1(fields)
		},
		[syncRowFieldsToV1],
	)

	// V3.4: Handle column fields change from PivotBuilder
	const handlePivotColumnFieldsChange = useCallback(
		(fields: ColumnField[]) => {
			if (fields.length === 0) {
				updateConfig({ columnFields: [], valueConfigs: [] })
				return
			}
			const needsValueInit = columnFields.length === 0 && fields.length > 0
			updateConfig({
				columnFields: fields,
				...(needsValueInit && {
					valueConfigs: [
						{
							field: 'response' as const,
							aggregation: AggregationFunction.AVG,
							label: 'Average Rating',
						},
					],
				}),
			})
		},
		[columnFields.length, updateConfig],
	)

	const doPivotOff = useCallback(() => {
		setPivotEnabled(false)
		updateConfig({ columnFields: [], valueConfigs: [] })
	}, [updateConfig])

	const togglePivotMode = useCallback(() => {
		if (pivotEnabled) {
			if (columnFields.length > 0) {
				pivotOffConfirmState.open()
			} else {
				doPivotOff()
			}
		} else {
			setPivotEnabled(true)
		}
	}, [pivotEnabled, columnFields.length, pivotOffConfirmState, doPivotOff])

	// V2.5: Handle column period change
	const handleColumnPeriodChange = useCallback(
		(period: GroupByPeriod) => {
			if (columnFields.length === 0) return
			const updatedColumnFields = [{ ...columnFields[0], period }]
			updateConfig({ columnFields: updatedColumnFields })
		},
		[columnFields, updateConfig],
	)

	// V3.1: Get current valueConfigs with fallback
	const currentValueConfigs = useMemo(() => {
		return (
			currentConfig.valueConfigs || [
				{ field: 'response' as const, aggregation: AggregationFunction.AVG, label: 'Average' },
			]
		)
	}, [currentConfig.valueConfigs])

	// V3.1: Add a new value aggregation
	const handleAddValueConfig = useCallback(() => {
		const newConfigs = [
			...currentValueConfigs,
			{ field: 'response' as const, aggregation: AggregationFunction.COUNT, label: 'Count' },
		]
		updateConfig({ valueConfigs: newConfigs })
	}, [currentValueConfigs, updateConfig])

	// V3.1: Remove a value aggregation
	const handleRemoveValueConfig = useCallback(
		(index: number) => {
			if (currentValueConfigs.length <= 1) return // Keep at least one
			const newConfigs = currentValueConfigs.filter((_, i) => i !== index)
			updateConfig({ valueConfigs: newConfigs })
		},
		[currentValueConfigs, updateConfig],
	)

	// V3.1: Update a value aggregation
	const handleUpdateValueConfig = useCallback(
		(index: number, aggregation: AggregationFunction) => {
			const newConfigs = [...currentValueConfigs]
			const labels: Record<AggregationFunction, string> = {
				[AggregationFunction.AVG]: 'Average',
				[AggregationFunction.COUNT]: 'Count',
				[AggregationFunction.SUM]: 'Sum',
				[AggregationFunction.MIN]: 'Minimum',
				[AggregationFunction.MAX]: 'Maximum',
			}
			newConfigs[index] = {
				...newConfigs[index],
				aggregation,
				label: labels[aggregation],
			}
			updateConfig({ valueConfigs: newConfigs })
		},
		[currentValueConfigs, updateConfig],
	)

	// V2.5: Check if column field is a date field
	const isColumnDateField = useMemo(() => {
		if (columnFields.length === 0) return false
		const systemField = GROUPABLE_SYSTEM_FIELDS.find((f) => f.id === columnFields[0].field)
		return systemField?.groupType === 'date_period'
	}, [columnFields])

	// Cardinality validation for column fields (Wrike #4374772881). Forwards
	// active filters so a "filter to reduce cardinality" suggestion actually drops
	// the count below threshold on refetch; enabled when either forms OR cycles
	// are selected so cycle-only configs (the headline use case for "leadership
	// reporting across review cycles") still get the warning.
	const cardinalityQuery = useQuery({
		...trpc.client.reportBuilder.validateColumnCardinality.queryOptions({
			fieldId: columnFields[0]?.field || '',
			source: columnFields[0]?.source || 'custom_field',
			period: columnFields[0]?.period,
			formIds: currentConfig.selectedFormIds,
			cycleIds: currentConfig.selectedCycleIds,
			filters: currentConfig.filters,
			filterLogic: currentConfig.filterLogic,
		}),
		enabled:
			columnFields.length > 0 &&
			!!columnFields[0]?.field &&
			(currentConfig.selectedFormIds.length > 0 || currentConfig.selectedCycleIds.length > 0),
	})

	// Get display label for a field
	const getFieldDisplayLabel = (field: AxisField) => {
		if (field.source === 'component_label') {
			if (field.field === QUESTION_LABEL_FIELD_ID) return 'Form Question'
			return `Category: ${field.field}`
		}
		const systemField = GROUPABLE_SYSTEM_FIELDS.find((f) => f.id === field.field)
		if (systemField) {
			if (systemField.groupType === 'date_period' && field.period) {
				const periodLabel = GROUP_BY_PERIOD_OPTIONS.find((p) => p.value === field.period)?.label || ''
				return `${systemField.label} (${periodLabel})`
			}
			return systemField.label
		}
		return field.field
	}

	// Check if a field at a level is a date field
	const isDateFieldAtLevel = (levelIndex: number) => {
		const field = rowFields[levelIndex]
		if (!field) return false
		const systemField = GROUPABLE_SYSTEM_FIELDS.find((f) => f.id === field.field)
		return systemField?.groupType === 'date_period'
	}

	// Can add more levels?
	const canAddLevel =
		rowFields.length < MAX_GROUPING_LEVELS && rowFields.length > 0 && rowFields[rowFields.length - 1]?.field !== ''

	// P0 Fix: Validation for incomplete hierarchy levels
	const hasIncompleteLevel = rowFields.some((f) => f.field === '')
	const isHierarchyValid = !hasIncompleteLevel
	const hierarchyValidationError = hasIncompleteLevel
		? 'Please select a field for all grouping levels or remove empty levels'
		: null

	if (isRawDataMode) {
		return (
			<div className='space-y-6'>
				<div>
					<h2 className='text-lg font-semibold text-neutral-900 mb-2'>Raw Data Export</h2>
					<p className='text-sm text-neutral-600'>
						Raw data exports include all individual responses without aggregation
					</p>
				</div>

				<div className='p-6 bg-blue-50 border border-blue-200 rounded-lg'>
					<div className='flex items-start gap-3'>
						<svg
							className='h-6 w-6 text-blue-600 flex-shrink-0 mt-0.5'
							fill='none'
							viewBox='0 0 24 24'
							stroke='currentColor'
							aria-hidden='true'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
							/>
						</svg>
						<div>
							<h4 className='text-sm font-semibold text-blue-900'>What's included in Raw Data exports:</h4>
							<ul className='mt-2 text-sm text-blue-800 space-y-1'>
								<li>• Each row represents one instance of a repeatable component</li>
								<li>• Participant information (name, email)</li>
								<li>• Form and cycle context</li>
								<li>• All field values from the selected repeatable components</li>
							</ul>
						</div>
					</div>
				</div>

				<div className='p-4 bg-neutral-50 border border-neutral-200 rounded-lg'>
					<h4 className='text-sm font-medium text-neutral-700 mb-2'>Export Configuration</h4>
					<div className='text-sm text-neutral-600'>
						<p>
							<span className='font-medium'>Report type:</span> Raw Data
						</p>
						<p>
							<span className='font-medium'>Selected repeatables:</span>{' '}
							{(currentConfig.selectedRepeatableIds || []).length}
						</p>
						<p>
							<span className='font-medium'>Cycle filter:</span>{' '}
							{currentConfig.selectedCycleIds.length > 0
								? `${currentConfig.selectedCycleIds.length} cycle(s)`
								: 'All cycles'}
						</p>
					</div>
				</div>

				<div className='flex justify-between'>
					<button
						type='button'
						onClick={prevStep}
						className='px-6 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 transition-colors font-medium'>
						Back
					</button>
					<button
						type='button'
						onClick={nextStep}
						className='px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium'>
						Next: Apply Filters
					</button>
				</div>
			</div>
		)
	}

	return (
		<div className='space-y-6'>
			<div>
				<h2 className='text-lg font-semibold text-neutral-900 mb-2'>Configure Grouping</h2>
				<p className='text-sm text-neutral-600'>
					Choose how to group and aggregate your report data. You can create a hierarchy with up to{' '}
					{MAX_GROUPING_LEVELS} levels.
				</p>
			</div>

			<div className='space-y-4'>
				<div>
					{/* V2.5: Pivot Mode Toggle */}
					<div className='mb-4 pb-4 border-b border-neutral-200'>
						<label className='flex items-center gap-3 cursor-pointer'>
							<input
								type='checkbox'
								checked={pivotEnabled}
								onChange={togglePivotMode}
								className='h-4 w-4 text-purple-600 border-neutral-300 rounded focus:ring-purple-500'
							/>
							<div>
								<span className='block text-sm font-medium text-neutral-900'>Enable Cross-Tab (Pivot)</span>
								<span className='block text-xs text-neutral-500'>
									Use drag-and-drop to configure pivot table rows and columns
								</span>
							</div>
						</label>
					</div>

					<div className='flex items-center justify-between mb-2'>
						<span className='block text-sm font-medium text-neutral-700'>
							{pivotEnabled ? 'Pivot Configuration' : 'Group By (Hierarchy)'}
						</span>
						{currentConfig.selectedFormIds.length > 0 && (
							<button
								ref={categoryManagerTriggerRef}
								type='button'
								data-testid='manage-categories-btn'
								onClick={() => categoryManagerState.open()}
								className='inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-blue-600 border border-blue-200 rounded hover:bg-blue-50 transition-colors'>
								<svg className='w-3.5 h-3.5' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
									<path
										strokeLinecap='round'
										strokeLinejoin='round'
										strokeWidth={2}
										d='M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A2 2 0 013 12V7a4 4 0 014-4z'
									/>
								</svg>
								Manage Categories
							</button>
						)}
					</div>
					<p className='text-xs text-neutral-500 mb-3'>
						{pivotEnabled
							? 'Drag fields between zones to configure your pivot table rows and columns.'
							: 'Build a hierarchy of grouping levels. Each level creates nested rows in your report.'}
					</p>

					{pivotEnabled ? (
						<div className='space-y-4'>
							<PivotBuilder
								availableFields={pivotAvailableFields}
								rowFields={rowFields}
								columnFields={columnFields}
								onRowFieldsChange={handlePivotRowFieldsChange}
								onColumnFieldsChange={handlePivotColumnFieldsChange}
							/>

							{/* Cardinality validation for column fields. Wrike #4374772881. */}
							{columnFields.length > 0 && !!columnFields[0]?.field && (
								<CardinalityDisplay isLoading={cardinalityQuery.isLoading} data={cardinalityQuery.data} />
							)}

							{/* Period selector for date column fields */}
							{isColumnDateField && columnFields.length > 0 && (
								<div className='p-3 bg-amber-50 border border-amber-200 rounded-lg'>
									<span className='block text-xs font-medium text-amber-900 mb-2'>Column Period</span>
									<div className='flex gap-1'>
										{GROUP_BY_PERIOD_OPTIONS.map((option) => (
											<button
												key={option.value}
												type='button'
												onClick={() => handleColumnPeriodChange(option.value)}
												className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
													columnFields[0]?.period === option.value
														? 'bg-amber-600 text-white'
														: 'bg-white border border-amber-300 text-amber-800 hover:bg-amber-100'
												}`}>
												{option.label}
											</button>
										))}
									</div>
								</div>
							)}

							{/* Value Aggregations */}
							{columnFields.length > 0 && (
								<div className='p-4 bg-purple-50 border border-purple-200 rounded-lg space-y-3'>
									<span className='block text-xs font-medium text-purple-900'>Value Aggregations</span>
									<div className='space-y-2'>
										{currentValueConfigs.map((vc, index) => (
											<div key={`${vc.aggregation}-${index}`} className='flex items-center gap-2'>
												<select
													value={vc.aggregation}
													onChange={(e) => handleUpdateValueConfig(index, e.target.value as AggregationFunction)}
													className='flex-1 px-2 py-1 text-sm border border-purple-300 rounded focus:outline-none focus:ring-1 focus:ring-purple-500 bg-white'>
													<option value={AggregationFunction.AVG}>Average (AVG)</option>
													<option value={AggregationFunction.COUNT}>Count</option>
													<option value={AggregationFunction.SUM}>Sum</option>
													<option value={AggregationFunction.MIN}>Minimum</option>
													<option value={AggregationFunction.MAX}>Maximum</option>
												</select>
												{currentValueConfigs.length > 1 && (
													<button
														type='button'
														onClick={() => handleRemoveValueConfig(index)}
														className='p-1 text-red-500 hover:bg-red-50 rounded'
														title='Remove aggregation'>
														<svg
															className='w-4 h-4'
															fill='none'
															viewBox='0 0 24 24'
															stroke='currentColor'
															aria-hidden='true'>
															<path
																strokeLinecap='round'
																strokeLinejoin='round'
																strokeWidth={2}
																d='M6 18L18 6M6 6l12 12'
															/>
														</svg>
													</button>
												)}
											</div>
										))}
										{currentValueConfigs.length < 3 && (
											<button
												type='button'
												onClick={handleAddValueConfig}
												className='flex items-center gap-1 px-2 py-1 text-xs font-medium text-purple-600 border border-dashed border-purple-300 rounded hover:bg-purple-50 transition-colors'>
												<svg
													className='w-3 h-3'
													fill='none'
													viewBox='0 0 24 24'
													stroke='currentColor'
													aria-hidden='true'>
													<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
												</svg>
												Add Aggregation
											</button>
										)}
									</div>
									{currentValueConfigs.length > 1 && (
										<p className='text-xs text-purple-600'>
											Multiple aggregations will create separate columns per value.
										</p>
									)}
								</div>
							)}

							{columnFields.length > 0 && (
								<div className='text-xs text-purple-700 bg-purple-100 p-2 rounded'>
									Pivot mode active. Results will show{' '}
									<strong>{rowFields.length > 0 ? 'row hierarchy' : 'all data'}</strong> with{' '}
									<strong>{columnFields[0].field}</strong> values as columns
									{currentValueConfigs.length > 1 && (
										<>
											{' '}
											with <strong>{currentValueConfigs.length} aggregations</strong>
										</>
									)}
									.
								</div>
							)}
						</div>
					) : (
						/* Dropdown-based Group By (non-pivot mode) */
						<div className='space-y-3'>
							{rowFields.length === 0 ? (
								// Initial state: show single dropdown
								<div className='flex items-center gap-2'>
									<select
										value=''
										onChange={(e) => handleFieldChange(0, e.target.value)}
										className='flex-1 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500'>
										<option value=''>No grouping</option>
										{availableGroupCategories.length > 0 && (
											<optgroup label='Component Categories'>
												{availableGroupCategories.map((category) => (
													<option key={`component_label.${category}`} value={`${COMPONENT_LABEL_PREFIX}${category}`}>
														{category}
													</option>
												))}
											</optgroup>
										)}
										{sortedCustomFields.length > 0 && (
											<optgroup label='User Attributes'>
												{sortedCustomFields.map((field) => (
													<option key={field} value={field}>
														{field}
													</option>
												))}
											</optgroup>
										)}
										{categoricalSystemFields.length > 0 && (
											<optgroup label='System Fields'>
												{categoricalSystemFields.map((field) => (
													<option key={field.id} value={field.id}>
														{field.label}
													</option>
												))}
											</optgroup>
										)}
										{dateSystemFields.length > 0 && (
											<optgroup label='Date Fields (by period)'>
												{dateSystemFields.map((field) => (
													<option key={field.id} value={field.id}>
														{field.label}
													</option>
												))}
											</optgroup>
										)}
									</select>
								</div>
							) : (
								// Show hierarchy with levels
								rowFields.map((field, index) => {
									const { availableCustom, availableCategorical, availableDate } = getAvailableFieldsForLevel(index)
									const isDateField = isDateFieldAtLevel(index)

									return (
										<div key={`${field.field}-${index}`} className='relative'>
											{/* Hierarchy indicator */}
											{index > 0 && <div className='absolute left-3 -top-3 w-px h-3 bg-neutral-300' />}
											<div
												className={`flex items-center gap-2 ${index > 0 ? 'ml-4' : ''}`}
												style={{ marginLeft: index * 16 }}>
												{index > 0 && <span className='text-neutral-400 text-sm'>└</span>}
												<span className='text-xs text-neutral-500 font-medium w-16'>Level {index + 1}:</span>
												<select
													value={
														field.source === 'component_label' ? `${COMPONENT_LABEL_PREFIX}${field.field}` : field.field
													}
													onChange={(e) => handleFieldChange(index, e.target.value)}
													className='flex-1 px-3 py-2 border border-neutral-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm'>
													<option value=''>Select field...</option>
													{/* Current selected field */}
													{field.field &&
														field.source !== 'component_label' &&
														!availableCustom.includes(field.field) &&
														!availableCategorical.some((f) => f.id === field.field) &&
														!availableDate.some((f) => f.id === field.field) && (
															<option value={field.field}>{getFieldDisplayLabel(field)}</option>
														)}
													{field.field &&
														field.source === 'component_label' &&
														!availableGroupCategories.includes(field.field) && (
															<option value={`${COMPONENT_LABEL_PREFIX}${field.field}`}>
																{getFieldDisplayLabel(field)}
															</option>
														)}
													{availableGroupCategories.length > 0 && (
														<optgroup label='Component Categories'>
															{availableGroupCategories
																.filter(
																	(cat) =>
																		!rowFields.some(
																			(rf, i) => i !== index && rf.source === 'component_label' && rf.field === cat,
																		),
																)
																.map((category) => (
																	<option
																		key={`component_label.${category}`}
																		value={`${COMPONENT_LABEL_PREFIX}${category}`}>
																		{category}
																	</option>
																))}
														</optgroup>
													)}
													{availableCustom.length > 0 && (
														<optgroup label='User Attributes'>
															{availableCustom.map((f) => (
																<option key={f} value={f}>
																	{f}
																</option>
															))}
														</optgroup>
													)}
													{availableCategorical.length > 0 && (
														<optgroup label='System Fields'>
															{availableCategorical.map((f) => (
																<option key={f.id} value={f.id}>
																	{f.label}
																</option>
															))}
														</optgroup>
													)}
													{availableDate.length > 0 && (
														<optgroup label='Date Fields (by period)'>
															{availableDate.map((f) => (
																<option key={f.id} value={f.id}>
																	{f.label}
																</option>
															))}
														</optgroup>
													)}
												</select>
												<button
													type='button'
													onClick={() => removeLevel(index)}
													className='p-1.5 text-neutral-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors'
													title='Remove level'>
													<svg
														className='w-4 h-4'
														fill='none'
														viewBox='0 0 24 24'
														stroke='currentColor'
														aria-hidden='true'>
														<path
															strokeLinecap='round'
															strokeLinejoin='round'
															strokeWidth={2}
															d='M6 18L18 6M6 6l12 12'
														/>
													</svg>
												</button>
											</div>

											{/* Period selector for date fields */}
											{isDateField && (
												<div className='ml-20 mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg'>
													<span className='block text-xs font-medium text-amber-900 mb-2'>Group Period</span>
													<div className='flex gap-1'>
														{GROUP_BY_PERIOD_OPTIONS.map((option) => (
															<button
																key={option.value}
																type='button'
																onClick={() => handlePeriodChange(index, option.value)}
																className={`px-3 py-1 rounded text-xs font-medium transition-colors ${
																	field.period === option.value
																		? 'bg-amber-600 text-white'
																		: 'bg-white border border-amber-300 text-amber-800 hover:bg-amber-100'
																}`}>
																{option.label}
															</button>
														))}
													</div>
												</div>
											)}
										</div>
									)
								})
							)}

							{/* Add Level button */}
							{canAddLevel && (
								<button
									type='button'
									onClick={addLevel}
									className='flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 border border-dashed border-blue-300 rounded-md hover:bg-blue-50 transition-colors'
									style={{ marginLeft: rowFields.length * 16 }}>
									<svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
										<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M12 4v16m8-8H4' />
									</svg>
									Add Level
								</button>
							)}
						</div>
					)}

					{/* Subtotals toggle (show when multiple row fields in either mode) */}
					{rowFields.length >= 2 && (
						<div className='mt-4 p-4 bg-neutral-50 border border-neutral-200 rounded-lg'>
							<label className='flex items-center gap-3 cursor-pointer'>
								<input
									type='checkbox'
									checked={currentConfig.showSubtotals ?? false}
									onChange={toggleSubtotals}
									className='h-4 w-4 text-blue-600 border-neutral-300 rounded focus:ring-blue-500'
								/>
								<div>
									<span className='block text-sm font-medium text-neutral-900'>Show Subtotals</span>
									<span className='block text-xs text-neutral-500'>Display subtotal rows for each grouping level</span>
								</div>
							</label>
							{currentConfig.showSubtotals && (
								<p className='mt-2 text-xs text-amber-600'>
									Subtotals are limited to 500 detail rows to prevent large result sets
								</p>
							)}
						</div>
					)}
				</div>
			</div>

			<div className='grid grid-cols-1 lg:grid-cols-2 gap-6'>
				<div>
					<span className='block text-sm font-medium text-neutral-700 mb-2'>Aggregate By</span>
					<p className='text-xs text-neutral-500 mb-3'>Select the types of aggregations to include</p>
					<div className='space-y-2'>
						{AGGREGATE_OPTIONS.map((option) => (
							<label
								key={option.value}
								className={`flex items-start p-3 border rounded-md cursor-pointer transition-all ${
									currentConfig.aggregateBy.includes(option.value)
										? 'border-blue-500 bg-blue-50'
										: 'border-neutral-200 hover:border-neutral-300'
								}`}>
								<input
									type='checkbox'
									checked={currentConfig.aggregateBy.includes(option.value)}
									onChange={() => toggleAggregate(option.value)}
									className='h-4 w-4 mt-0.5 text-blue-600 border-neutral-300 rounded focus:ring-blue-500'
								/>
								<div className='ml-3'>
									<span className='block text-sm font-medium text-neutral-900'>{option.label}</span>
									<span className='block text-xs text-neutral-500'>{option.description}</span>
								</div>
							</label>
						))}
					</div>
				</div>

				<div className='p-4 bg-neutral-50 border border-neutral-200 rounded-lg'>
					<h4 className='text-sm font-medium text-neutral-700 mb-2'>Preview Configuration</h4>
					<div className='text-sm text-neutral-600'>
						<p>
							<span className='font-medium'>Row grouping:</span>{' '}
							{rowFields.length > 0 ? rowFields.map((f) => getFieldDisplayLabel(f)).join(' → ') : 'No grouping'}
						</p>
						{rowFields.length >= 2 && (
							<p>
								<span className='font-medium'>Subtotals:</span> {currentConfig.showSubtotals ? 'Enabled' : 'Disabled'}
							</p>
						)}
						{columnFields.length > 0 && (
							<p>
								<span className='font-medium'>Column axis:</span> {getFieldDisplayLabel(columnFields[0])} (Pivot mode)
							</p>
						)}
						<p>
							<span className='font-medium'>Aggregations:</span>{' '}
							{currentConfig.aggregateBy.length > 0 ? currentConfig.aggregateBy.join(', ') : 'None selected'}
						</p>
					</div>
				</div>
			</div>

			{/* P0 Fix: Validation error message for incomplete levels */}
			{hierarchyValidationError && (
				<div className='p-3 bg-red-50 border border-red-200 rounded-lg'>
					<p className='text-sm text-red-700 flex items-center gap-2'>
						<svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z'
							/>
						</svg>
						{hierarchyValidationError}
					</p>
				</div>
			)}

			{/* Multi-level grouping info banner */}
			{rowFields.length >= 2 && !pivotEnabled && (
				<div className='p-3 bg-blue-50 border border-blue-200 rounded-lg'>
					<p className='text-sm text-blue-700 flex items-center gap-2'>
						<svg className='w-4 h-4' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								strokeWidth={2}
								d='M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
							/>
						</svg>
						Multi-level grouping configured. Enable subtotals above for hierarchical output.
					</p>
				</div>
			)}

			<div className='flex justify-between'>
				<button
					type='button'
					onClick={prevStep}
					className='px-6 py-2 border border-neutral-300 text-neutral-700 rounded-md hover:bg-neutral-50 transition-colors font-medium'>
					Back
				</button>
				<button
					type='button'
					onClick={nextStep}
					disabled={currentConfig.aggregateBy.length === 0 || !isHierarchyValid}
					className='px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed'>
					Next: Apply Filters
				</button>
			</div>

			{categoryManagerState.isOpen && <CategoryManagerModal state={categoryManagerState} />}

			<ConfirmDialog
				title='Disable Pivot Mode'
				message='This will clear your column fields and value aggregations. Continue?'
				confirmLabel='Disable Pivot'
				onConfirm={doPivotOff}
				state={pivotOffConfirmState}
			/>
		</div>
	)
}

function CategoryManagerModal({ state }: { state: ReturnType<typeof useOverlayTriggerState> }) {
	const overlayRef = useRef<HTMLDivElement>(null)
	const dialogRef = useRef<HTMLDivElement>(null)

	const { modalProps, underlayProps } = useModalOverlay({ isDismissable: true }, state, overlayRef)

	const { dialogProps, titleProps } = useDialog({ role: 'dialog' }, dialogRef)

	return (
		<div className='fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4' {...underlayProps}>
			<div
				ref={overlayRef}
				{...modalProps}
				data-testid='category-manager-modal'
				className='bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-y-auto'>
				<div ref={dialogRef} {...dialogProps}>
					<div className='sticky top-0 bg-white border-b border-neutral-200 px-6 py-4 flex items-center justify-between'>
						<h2 {...titleProps} className='text-lg font-semibold text-neutral-900'>
							Manage Component Categories
						</h2>
						<IconButton
							label='Close'
							onPress={() => state.close()}
							className='p-1.5 text-neutral-400 hover:text-neutral-600 hover:bg-neutral-100 rounded transition-colors'>
							<svg className='w-5 h-5' fill='none' viewBox='0 0 24 24' stroke='currentColor' aria-hidden='true'>
								<path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
							</svg>
						</IconButton>
					</div>
					<div className='p-6'>
						<CategoryManager />
					</div>
				</div>
			</div>
		</div>
	)
}
